import React, { useCallback } from "react";
import { useParams } from "react-router-dom";

// @ts-ignore - JSX コンポーネント（型定義なし）
import TitleBlock from "../../../components/block/TitleBlock";
import SvgLoadingAnimation from "../../../components/loading/index";
import Select, { type SelectOption } from "../../../components/form-item/Select";
import StatusIndicator from "../../../components/status-indicator/StatusIndicator";
import {
  useGetProjectQuery,
  type TaskBoardAccess,
} from "../../../app/services/projects";
import { useUpdateTaskBoardAccessMutation } from "../../../app/services/taskBoard";
import { useStatusIndicator } from "../../roles/hooks/useStatusIndicator";

/**
 * プロジェクト INFO 内「タスクボード共有」ページ。
 *
 * タスクボード（スケジュール）の共有範囲を役割（ポジション）単位で設定する。
 * 責任者プロデューサー（PRODUCER）・管理者は常に編集でき、共有設定を変更できるのも
 * この 2 者のみ（`task_board_can_manage`）。クライアント責任者・ディレクターには
 * 「更新可 / 閲覧のみ / 共有しない」を割り当てられる。権限は INFO タブ表示条件と
 * バックエンド API の双方でガードする。
 */
const ACCESS_OPTIONS: SelectOption[] = [
  { value: "edit", name: "更新可" },
  { value: "view", name: "閲覧のみ" },
  { value: "none", name: "共有しない" },
];

const ProjectTaskBoardAccess: React.FC = () => {
  const { projectId } = useParams();

  const { data, isLoading } = useGetProjectQuery(
    { project_id: projectId ?? "" },
    { skip: !projectId },
  );
  const project = data?.data?.project;
  const directorAccess: TaskBoardAccess =
    project?.task_board_director_access ?? "edit";
  const clientAccess: TaskBoardAccess =
    project?.task_board_client_access ?? "none";
  const canManage = project?.task_board_can_manage ?? false;

  const [updateAccess, { isLoading: isSaving }] =
    useUpdateTaskBoardAccessMutation();
  const { status, indicate } = useStatusIndicator();

  const handleChange = useCallback(
    async (field: "director_access" | "client_access", value: string) => {
      if (!projectId || !canManage || isSaving) return;
      indicate("saving");
      try {
        await updateAccess({
          project_id: projectId,
          ...(field === "director_access"
            ? { director_access: value as TaskBoardAccess }
            : { client_access: value as TaskBoardAccess }),
        }).unwrap();
        indicate("saved");
      } catch {
        indicate("error");
      }
    },
    [projectId, canManage, isSaving, updateAccess, indicate],
  );

  if (isLoading) return <SvgLoadingAnimation />;

  return (
    <div className="u-col u-w100 u-gap16">
      <TitleBlock
        title="タスクボード共有"
        description="タスクボード（スケジュール）の共有範囲を役割ごとに設定します。責任者プロデューサー・管理者は常に編集できます。クライアント責任者・ディレクターには「更新可 / 閲覧のみ / 共有しない」を割り当てられます。"
      />

      {!canManage && (
        <p className="bodytext-13 u-text-light-gray">
          共有設定を変更できるのは責任者プロデューサー・管理者のみです（現在の設定を表示しています）。
        </p>
      )}

      <div className="u-col u-w100 u-gap16">
        <div className="u-col u-w100 u-gap8">
          <span className="bodytext-13">クライアント（責任者）</span>
          <Select
            options={ACCESS_OPTIONS}
            value={clientAccess}
            onChange={(e) => void handleChange("client_access", e.target.value)}
            disabled={!canManage || isSaving}
          />
        </div>

        <div className="u-col u-w100 u-gap8">
          <span className="bodytext-13">ディレクター</span>
          <Select
            options={ACCESS_OPTIONS}
            value={directorAccess}
            onChange={(e) => void handleChange("director_access", e.target.value)}
            disabled={!canManage || isSaving}
          />
        </div>
      </div>

      <p className="bodytext-11 u-text-light-gray">
        「更新可」にするとタスクの追加・編集や期限のドラッグ変更も可能になります。運用メモを共有するだけなら「閲覧のみ」を推奨します。
      </p>

      <StatusIndicator status={status} />
    </div>
  );
};

export default ProjectTaskBoardAccess;
